import type { PublicSettingsOut } from "@saquei/types";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * Aplica cores e display name customizados do tenant ao DOM.
 * Idempotente -- pode ser chamado a cada fetch das settings sem efeitos colaterais.
 *
 * Display name: prioriza `brand_display_name` (override custom) -> `tenant_name`
 * (do tenants.name no DB) -> nada. Frontend cai em fallback do subdomain
 * capitalizado se ainda nao houver.
 *
 * Cor invalida (formato fora de #RRGGBB) e ignorada -- vale o default
 * saquei laranja vindo de tokens.css.
 */
export function applyBrand(out: PublicSettingsOut | null | undefined): void {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const settings = out as any;

    if (settings?.brand_primary_color && HEX_RE.test(settings.brand_primary_color)) {
        root.style.setProperty("--color-brand-primary", settings.brand_primary_color);
        const hover = darken(settings.brand_primary_color, 0.08);
        root.style.setProperty("--color-brand-primary-hover", hover);
    } else {
        root.style.removeProperty("--color-brand-primary");
        root.style.removeProperty("--color-brand-primary-hover");
    }

    const displayName =
        settings?.brand_display_name?.trim() || settings?.tenant_name?.trim();
    if (displayName) {
        root.dataset.tenantName = displayName;
    } else {
        delete root.dataset.tenantName;
    }
}

/** Retorna o display name efetivo (custom ou fallback). */
export function effectiveDisplayName(
    settings: TenantSettings | null | undefined,
    fallback: string,
): string {
    return settings?.brand_display_name?.trim() || fallback;
}

/** Retorna logo URL ou null. Component decide se mostra texto ou imagem. */
export function effectiveLogoUrl(settings: TenantSettings | null | undefined): string | null {
    const u = settings?.brand_logo_url?.trim();
    return u ? u : null;
}

/** Mistura `factor` com preto. Ex: 0.08 = 8% mais escuro. */
function darken(hex: string, factor: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const k = 1 - factor;
    const rr = Math.max(0, Math.round(r * k)).toString(16).padStart(2, "0");
    const gg = Math.max(0, Math.round(g * k)).toString(16).padStart(2, "0");
    const bb = Math.max(0, Math.round(b * k)).toString(16).padStart(2, "0");
    return `#${rr}${gg}${bb}`;
}
